<template>
  <div
    class="anz-mb-xs anz-mb-xl-sm anz-flex anz-align-items-center anz-bt-mo anz-bb-mo"
  >
    <a-button
      class-name="anz-tabs-arrow anz-tabs-arrow--prev"
      icon-name="chevron-up"
      show-icon
      icon-width="15px"
      icon-height="15px"
      :class="{
        'anz-hidden': !activeCarousel,
        'is-inactive': !activeFirstItem,
      }"
      ref="carouselPrev"
      @click="moveToPrevSlide()"
    />
    <div class="anz-tabs-carousel" ref="carousel">
      <ul class="anz-ul anz-flex anz-tabs" ref="carouselWrapper">
        <profile-menu-item
          @click="showAll"
          :active="isMenuPostsActive"
          :count="profile.stats.visiblePosts"
          :title="$translate('profile.labels.posts')"
          id="AllPosts"
        />
        <profile-menu-item
          v-if="isShowingCurrentUser"
          @click="showUnseenReplies"
          :active="isMenuReactionsActive"
          :count="profile.stats.postRepliesUnseen"
          :title="$translate('profile.labels.postRepliesUnseenMenu')"
          id="UnseenReplies"
        />
        <profile-menu-item
          v-if="isShowingCurrentUser"
          @click="showSuperLike"
          :active="isSuperLike"
          :count="profile.stats.superLikesGivenTotal"
          :title="$translate('profile.labels.superLike')"
          id="SuperLike"
        />
        <profile-menu-item
          @click="showFollowers"
          :active="isFollowers"
          :count="profile.stats.followers"
          :title="
            isShowingCurrentUser
              ? $translate('profile.labels.followers')
              : $translate('profile.labels.followersVisitor')
          "
          id="Followers"
        />
        <profile-menu-item
          @click="showSubscribers"
          :active="isSubscribers"
          :count="profile.stats.subscribedUsers"
          :title="
            isShowingCurrentUser
              ? $translate('profile.labels.subscribers')
              : $translate('profile.labels.subscribersVisitor')
          "
          id="Subscribers"
        />
        <profile-menu-item
          v-if="isShowingCurrentUser"
          @click="showReported"
          :active="isReported"
          :count="profile.stats.subscribedUsers"
          :title="$translate('profile.labels.reportedPosts')"
          id="Reported"
        />

        <profile-menu-item
          v-if="isShowingCurrentUser"
          @click="showBlacklist"
          :active="isBlacklist"
          :count="profile.stats.blockedUsers"
          :title="$translate('profile.labels.blackList')"
          id="Blacklist"
        />
      </ul>
    </div>
    <a-button
      class-name="anz-tabs-arrow anz-tabs-arrow--next"
      icon-name="chevron-up"
      show-icon
      icon-width="15px"
      icon-height="15px"
      :class="{ 'anz-hidden': !activeCarousel, 'is-inactive': !activeLastItem }"
      ref="carouselNext"
      @click="moveToNextSlide()"
    />
  </div>
  <!-- Content tabs [Posts] -->
  <div
    class="anz-mt-xl anz-mt-0-sm anz-mb-s-sm anz-pb-xs anz-ml-l-sm"
    v-if="isShowingCurrentUser && isMenuPostsActive"
  >
    <ul class="anz-inline-list-bar anz-font-bold">
      <profile-sub-menu-item
        @click="showAll"
        :active="isAllPosts"
        :count="profile.stats.visiblePosts"
      >
        <template #title>
          <span class="anz-hidden-mo">{{
            $translate('post.actions.allPosts')
          }}</span>
          <span class="anz-hidden-sm">{{
            $translate('post.actions.allPostsShort')
          }}</span>
        </template>
      </profile-sub-menu-item>
      <profile-sub-menu-item
        @click="showHiddenPosts"
        :active="isHiddenPosts"
        :count="profile.stats.hiddenPosts"
        :title="$translate('profile.labels.blocked')"
      />
    </ul>
  </div>
  <!-- Content tabs [Post reactions] -->
  <div
    class="anz-mt-xl anz-mt-0-sm anz-mb-s-sm anz-pb-xs anz-ml-l-sm"
    v-else-if="isShowingCurrentUser && isMenuReactionsActive"
  >
    <ul class="anz-inline-list-bar anz-font-bold">
      <profile-sub-menu-item
        @click="showUnseenReplies"
        :active="isUnseenRepliesPosts"
        :count="profile.stats.postRepliesUnseen"
        :title="$translate('profile.labels.postRepliesUnseen')"
      />

      <profile-sub-menu-item
        @click="showAllRepliesPosts"
        :active="isAllRepliesPosts"
        :count="profile.stats.postReplies"
        :title="$translate('profile.labels.postReplies')"
      />
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import AButton from '@/components/Forms/AButton.vue'
import { useProfileShow } from '@/composables/user/user'
import useProfileMenu, {
  useProfileMenuChange,
} from '@/composables/profile/profileMenu'
import ProfileMenuItem from '@/components/Profile/Menu/ProfileMenuItem.vue'
import ProfileSubMenuItem from '@/components/Profile/Menu/ProfileSubMenuItem.vue'

export default defineComponent({
  name: 'UserProfileMenu',
  components: { ProfileSubMenuItem, ProfileMenuItem, AButton },
  data() {
    return {
      activeCarousel: false,
      activeFirstItem: false,
      activeLastItem: false,
    }
  },
  methods: {
    isCarouselActive() {
      const carousel: any = this.$refs.carousel
      const carouselWrapper: any = this.$refs.carouselWrapper

      if (
        typeof carousel === 'undefined' &&
        typeof carouselWrapper === 'undefined'
      ) {
        return false
      }

      return (this.activeCarousel =
        carouselWrapper.scrollWidth > carousel.clientWidth)
    },

    moveToPrevSlide() {
      const carousel: any = this.$refs.carousel
      const allVisibleItems = carousel.querySelectorAll('.item-visible')
      const firstVisibleItem = allVisibleItems[0]
      const prevItem = firstVisibleItem.previousElementSibling

      if (prevItem !== null) {
        prevItem.classList.add('item-visible')

        carousel.scroll({
          left: prevItem.offsetLeft,
          behavior: 'smooth',
        })
      }
    },

    initSlider() {
      const carousel: any = this.$refs.carousel
      const carouselWrapper: any = this.$refs.carouselWrapper
      const currentActivePage = useProfileMenu().activePage.value
      const carouselItems: any = carouselWrapper.querySelectorAll(
        '.anz-tabs-carousel__item'
      )

      function getActiveItem() {
        carouselItems.forEach((item: any) => {
          const itemId = item.getAttribute('id')
          if (itemId === currentActivePage) {
            item.classList.add('item-visible')

            carousel.scrollLeft = item.offsetLeft
          }
        })
      }

      if (document.readyState === 'complete') {
        getActiveItem()
      } else {
        window.addEventListener('load', function() {
          getActiveItem()
        })
      }
    },

    moveToNextSlide() {
      const carousel: any = this.$refs.carousel
      const allVisibleItems = carousel.querySelectorAll('.item-visible')
      const lastVisibleItem = allVisibleItems[allVisibleItems.length - 1]
      const nextItem = lastVisibleItem.nextElementSibling

      if (nextItem !== null) {
        const nextItemOffsetRight = nextItem.offsetLeft + nextItem.offsetWidth
        nextItem.classList.add('item-visible')

        carousel.scroll({
          left: nextItemOffsetRight - carousel.clientWidth,
          behavior: 'smooth',
        })
      }
    },

    setVisibleSlides() {
      const carouselWrapper: any = this.$refs.carouselWrapper
      const carouselItems: any = carouselWrapper.querySelectorAll(
        '.anz-tabs-carousel__item'
      )

      carouselItems.forEach((item: any) => {
        const observer = new IntersectionObserver(
          entries => {
            if (entries[0].isIntersecting) {
              item.classList.add('item-visible')

              this.activeFirstItem = !carouselItems[0].classList.contains(
                'item-visible'
              )

              this.activeLastItem = !carouselItems[
                carouselItems.length - 1
              ].classList.contains('item-visible')
            } else {
              item.classList.remove('item-visible')
            }
          },
          { threshold: [1] }
        )

        observer.observe(item)
      })
    },

    setCarouselDraggable() {
      const carousel: any = this.$refs.carousel
      let mouseDown = false
      let startX: any
      let scrollLeft: any
      let touchStartX: any

      if (carousel !== null) {
        const startDragging = (e: any) => {
          mouseDown = true
          startX = e.pageX - carousel.offsetLeft
          scrollLeft = carousel.scrollLeft
        }

        const startTouchDragging = (e: any) => {
          mouseDown = true
          touchStartX = e.changedTouches[0].pageX - carousel.offsetLeft
          scrollLeft = carousel.scrollLeft
        }

        const stopDragging = () => {
          mouseDown = false
        }

        carousel.addEventListener('mousemove', function(e: any) {
          e.preventDefault()

          if (!mouseDown) {
            return
          }

          const x = e.pageX - carousel.offsetLeft
          const scroll = x - startX

          carousel.scrollLeft = scrollLeft - scroll
        })

        carousel.addEventListener('touchmove', function(e: any) {
          e.preventDefault()

          if (!mouseDown) {
            return
          }

          const x = e.changedTouches[0].pageX - carousel.offsetLeft
          const scroll = x - touchStartX

          carousel.scrollLeft = scrollLeft - scroll
        })

        carousel.addEventListener('mousedown', startDragging, false)
        carousel.addEventListener('mouseup', stopDragging, false)
        carousel.addEventListener('mouseleave', stopDragging, false)

        carousel.addEventListener('touchstart', startTouchDragging, false)
        carousel.addEventListener('touchend', stopDragging, false)
        carousel.addEventListener('touchcancel', stopDragging, false)
      }
    },
  },
  mounted() {
    this.initSlider()
    this.setVisibleSlides()
    this.setCarouselDraggable()

    this.isCarouselActive()

    window.addEventListener('resize', () => {
      this.isCarouselActive()
    })
  },
  setup() {
    return {
      ...useProfileMenuChange(),
      ...useProfileShow(),
      ...useProfileMenu(),
    }
  },
})
</script>
